import {
  CHAPTER_ONE,
  RULES,
  buildScenarioOpponentPlan,
  cloneState,
  createScenarioGame,
  getCardTargets,
  getDevotionTargets,
  getReplayableCard,
  getScenarioResultKey,
  getScenarioTutorialRule,
  legalFocusLanes,
  resolveHour,
  validateScenarioPlan,
  type Depth,
  type GameState,
  type HourResolution,
  type Lane,
  type PlayerIndex,
  type PlayerPlan,
  type ScenarioDefinition,
} from "../../shared/index.js";
import {
  getCard,
  getDevotion,
  getServant,
  type CardDefinition,
  type DevotionDefinition,
} from "../../shared/content/index.js";
import type {
  BattlePhase,
  BattleSession,
  BattleUiState,
} from "./BattleSession.js";

export class ChapterBattleSession implements BattleSession {
  public readonly mode = "story" as const;
  public readonly viewerIndex = 0 as const;
  public state: GameState;
  public draft: PlayerPlan;
  public phase: BattlePhase = "planning";
  public error = "";

  private presentationState: GameState | null = null;

  public constructor(public readonly scenario: ScenarioDefinition = CHAPTER_ONE) {
    this.state = createScenarioGame(scenario);
    this.draft = this.createDraft();
  }

  public get scenarioId(): string {
    return this.scenario.id;
  }

  public get startLog(): string {
    return this.scenario.startLog;
  }

  public reset(): void {
    this.state = createScenarioGame(this.scenario);
    this.phase = "planning";
    this.error = "";
    this.presentationState = null;
    this.draft = this.createDraft();
  }

  public get selectedCard(): CardDefinition | null {
    return this.draft.supplyCardId ? getCard(this.draft.supplyCardId) : null;
  }

  public get selectedDevotion(): DevotionDefinition | null {
    return this.draft.devotionId ? getDevotion(this.draft.devotionId) : null;
  }

  public isCardAllowed(cardId: string): boolean {
    const rule = getScenarioTutorialRule(this.scenario, this.state.turn);
    if (!rule) return true;
    if (rule.requiredDevotionId) return false;
    return !rule.requiredCardId || rule.requiredCardId === cardId;
  }

  public selectCard(cardId: string): void {
    if (this.phase !== "planning" || !this.isSupplyCardSelectable(cardId)) return;
    this.error = "";
    if (this.draft.supplyCardId === cardId) {
      delete this.draft.supplyCardId;
      this.draft.target = {};
      return;
    }
    this.draft.cardInstanceId = "";
    this.draft.supplyCardId = cardId;
    this.draft.devotionId = "";
    this.draft.target = {};
  }

  public selectDevotion(): void {
    if (this.phase !== "planning") return;
    const player = this.state.players[0];
    const servant = getServant(player.servantId);
    const rule = getScenarioTutorialRule(this.scenario, this.state.turn);
    if (!servant || player.devotionUsed || rule?.requiredCardId
      || getDevotionTargets(this.state, 0).length === 0) return;
    this.error = "";
    if (this.draft.devotionId === servant.devotion.id) {
      this.draft.devotionId = "";
      this.draft.target = {};
      return;
    }
    this.draft.cardInstanceId = "";
    delete this.draft.supplyCardId;
    this.draft.devotionId = servant.devotion.id;
    this.draft.target = {};
  }

  public selectDevotionTarget(playerIndex: PlayerIndex): void {
    if (this.phase !== "planning" || this.selectedDevotion?.targetType !== "player") return;
    this.error = "";
    this.draft.target = { playerIndex };
  }

  public selectFocus(lane: Lane): void {
    if (this.phase !== "planning") return;
    const legal = legalFocusLanes(this.state, 0);
    const required = getScenarioTutorialRule(this.scenario, this.state.turn)?.requiredFocusLane;
    if (!legal.includes(lane)) {
      this.error = "現在の注目からは、隣のレーンまでしか移れません。";
      return;
    }
    if (required && required !== lane) {
      this.error = "この時間はレーン" + required + "へ注目します。";
      return;
    }
    this.error = "";
    this.draft.focusLane = lane;
  }

  public selectBoardPosition(lane: Lane, depth: Depth, objectId?: string): void {
    if (this.phase !== "planning") return;
    const devotion = this.selectedDevotion;
    const card = this.selectedCard
      ?? (devotion?.targetType === "replay" ? getReplayableCard(this.state, 0) : null);
    this.error = "";

    if (devotion?.targetType === "object") {
      if (objectId) this.draft.target = { objectId };
      return;
    }
    if (card?.kind === "object") {
      this.draft.target = { lane };
      return;
    }
    if (!card || card.kind !== "spell") {
      this.selectFocus(lane);
      return;
    }

    if (card.targetType === "none") {
      this.draft.target = {};
      return;
    }
    if (card.targetType === "lane") {
      this.draft.target = { lane };
      return;
    }
    if (card.targetType === "depth") {
      this.draft.target = { depth };
      return;
    }
    if (card.targetType === "object") {
      if (objectId) this.draft.target = { objectId };
      return;
    }

    const source = this.state.board.find((object) => object.id === this.draft.target.objectId);
    if (source) {
      if (card.targetType === "object-direction") {
        const direction = lane - source.lane;
        if (depth === source.depth && (direction === -1 || direction === 1)) {
          this.draft.target = { objectId: source.id, direction };
          return;
        }
      }
      if (card.targetType === "object-depth"
        && lane === source.lane
        && Math.abs(depth - source.depth) === 1) {
        this.draft.target = { objectId: source.id, depth };
        return;
      }
      if (objectId === source.id) return;
    }
    if (objectId) this.draft.target = { objectId };
  }

  public prepareResolution(isPass: boolean): HourResolution | null {
    if (this.phase !== "planning" || this.state.result) return null;
    const proposed = isPass
      ? this.passPlan()
      : clonePlan(this.draft);
    const validation = validateScenarioPlan(this.state, this.scenario, proposed, isPass);
    if (!validation.ok) {
      this.error = validation.reason;
      return null;
    }

    this.error = "";
    this.presentationState = cloneState(this.state);
    this.phase = "resolving";
    const opponentPlan = buildScenarioOpponentPlan(this.state, this.scenario);
    return resolveHour(this.state, [proposed, opponentPlan]);
  }

  public finishResolution(): void {
    this.presentationState = null;
    if (this.state.result) {
      this.phase = "result";
      return;
    }
    this.phase = "planning";
    this.draft = this.createDraft();
  }

  public getUiState(): BattleUiState {
    const displayState = this.presentationState ?? this.state;
    const actionSelected = Boolean(this.draft.supplyCardId || this.draft.devotionId);
    const actionValidation = validateScenarioPlan(
      this.state,
      this.scenario,
      this.draft,
      false,
    );
    const passValidation = validateScenarioPlan(
      this.state,
      this.scenario,
      this.passPlan(),
      true,
    );
    const tutorialRule = getScenarioTutorialRule(this.scenario, this.state.turn);
    const legalLanes = legalFocusLanes(this.state, 0);
    const servant = getServant(this.state.players[0].servantId);
    const supply = this.state.openSupply;
    const displaySupply = displayState.openSupply;
    return {
      mode: this.mode,
      viewerIndex: this.viewerIndex,
      cardSource: "supply",
      state: cloneState(displayState),
      draft: clonePlan(this.draft),
      phase: this.phase,
      legalFocusLanes: tutorialRule?.requiredFocusLane
        ? legalLanes.filter((lane) => lane === tutorialRule.requiredFocusLane)
        : legalLanes,
      allowedCardInstanceIds: [],
      supplyCards: (supply?.cards ?? []).map((entry) => ({
        cardId: entry.cardId,
        cost: entry.cost,
        limited: entry.limited,
        available: this.phase === "planning" && this.isSupplyCardSelectable(entry.cardId),
        opponentAvailable: entry.availableTo[1],
      })),
      energy: displaySupply ? [...displaySupply.energy] as [number, number] : null,
      maxEnergy: supply?.maxEnergy ?? RULES.openSupplyMaxEnergy,
      devotionAvailable: this.phase === "planning"
        && !this.state.players[0].devotionUsed
        && Boolean(servant)
        && getDevotionTargets(this.state, 0).length > 0
        && !tutorialRule?.requiredCardId,
      tutorialPrompt: this.getTutorialPrompt(),
      targetPrompt: this.getTargetPrompt(),
      error: this.error,
      canSubmit: this.phase === "planning" && actionSelected && actionValidation.ok,
      canPass: this.phase === "planning" && passValidation.ok,
      resultKey: this.state.result ? getScenarioResultKey(this.state) : null,
      connected: [true, true],
      submitted: [false, false],
    };
  }

  private createDraft(): PlayerPlan {
    const requiredFocus = getScenarioTutorialRule(
      this.scenario,
      this.state.turn,
    )?.requiredFocusLane;
    const focusLane = requiredFocus
      && legalFocusLanes(this.state, 0).includes(requiredFocus)
      ? requiredFocus
      : this.state.players[0].focusLane;
    return {
      focusLane,
      cardInstanceId: "",
      devotionId: "",
      target: {},
    };
  }

  private passPlan(): PlayerPlan {
    return {
      focusLane: this.draft.focusLane,
      cardInstanceId: "",
      devotionId: "",
      target: {},
    };
  }

  private getTutorialPrompt(): string {
    if (this.phase === "resolving") {
      return "二人の手つきを夢へ反映しています。";
    }
    if (this.phase === "result") return "夜明けを迎えました。";
    return "カードか献身を選び、今夜の夢へ触れる";
  }

  private getTargetPrompt(): string {
    if (this.error) return this.error;
    if (this.phase === "resolving") return "二つの計画を、順番に夢へ反映しています。";
    if (this.phase === "result") return "夜明けを迎えました。";

    const card = this.selectedCard;
    const devotion = this.selectedDevotion;
    if (devotion) {
      if (devotion.targetType === "none") {
        return "献身《" + devotion.name + "》を捧げます。";
      }
      if (devotion.targetType === "player") {
        const targetIndex = this.draft.target.playerIndex;
        if (targetIndex !== 0 && targetIndex !== 1) {
          return "献身を向けるご主人を選んでください。";
        }
        const target = this.state.players[targetIndex];
        const after = target.happiness + Math.sign(RULES.idealHappiness - target.happiness) * 3;
        return target.name + "へ注ぎ、幸福度を " + target.happiness + " → " + after
          + " にします。次の時間は注目が固定されます。";
      }
      if (devotion.targetType === "replay") {
        const replayCard = getReplayableCard(this.state, 0);
        if (!replayCard) return "直前の時間に再演できるカードがありません。";
        return this.getCardTargetPrompt(replayCard, 0, true);
      }
      const object = this.state.board.find(
        (item) => item.id === this.draft.target.objectId,
      );
      return object
        ? object.name + "へ献身《" + devotion.name + "》を捧げます。"
        : "献身を向ける夢オブジェクトを選んでください。";
    }
    if (!card) return "演出卓の札か献身を選んでください。盤面上部では注目レーンを選べます。";
    const cost = this.state.openSupply?.cards.find((entry) => entry.cardId === card.id)?.cost ?? 0;
    return this.getCardTargetPrompt(card, cost, false);
  }

  private isSupplyCardSelectable(cardId: string): boolean {
    if (!this.isCardAllowed(cardId)) return false;
    const supply = this.state.openSupply;
    const entry = supply?.cards.find((item) => item.cardId === cardId);
    const card = getCard(cardId);
    if (!supply || !entry || !card || !entry.availableTo[0]) return false;
    if (supply.energy[0] < entry.cost) return false;
    return getCardTargets(this.state, card.id).length > 0;
  }

  private getCardTargetPrompt(card: CardDefinition, cost: number, replay: boolean): string {
    const lead = replay ? "再演する" : "演出力" + cost + "で";
    if (card.kind === "object") {
      return this.draft.target.lane
        ? lead + "《" + card.name + "》をレーン" + this.draft.target.lane + "の深度3へ置きます。"
        : "置きたいレーンの盤面をクリックしてください。";
    }
    if (card.targetType === "none") {
      return lead + "《" + card.name + "》を盤面全体へ使います。";
    }
    if (card.targetType === "lane") {
      return this.draft.target.lane
        ? lead + "《" + card.name + "》をレーン" + this.draft.target.lane + "へ使います。"
        : "対象にするレーンの盤面をクリックしてください。";
    }
    if (card.targetType === "depth") {
      return this.draft.target.depth
        ? lead + "《" + card.name + "》を深度" + this.draft.target.depth + "へ使います。"
        : "対象にする深度の盤面をクリックしてください。";
    }
    const object = this.state.board.find((item) => item.id === this.draft.target.objectId);
    if (!object) return "対象の夢オブジェクトをクリックしてください。";
    if (card.targetType === "object-direction" && !this.draft.target.direction) {
      return object.name + "を選択中。左右どちらかの隣のマスをクリックしてください。";
    }
    if (card.targetType === "object-direction") {
      return object.name + "を" + (this.draft.target.direction === -1 ? "左" : "右") + "へ動かします。";
    }
    if (card.targetType === "object-depth" && !this.draft.target.depth) {
      return object.name + "を選択中。同じレーンの隣の深度をクリックしてください。";
    }
    if (card.targetType === "object-depth") {
      return object.name + "を深度" + this.draft.target.depth + "へ動かします。";
    }
    return object.name + "へ《" + card.name + "》を使います。";
  }
}

function clonePlan(plan: PlayerPlan): PlayerPlan {
  const cloned: PlayerPlan = {
    focusLane: plan.focusLane,
    cardInstanceId: plan.cardInstanceId,
    devotionId: plan.devotionId,
    target: { ...plan.target },
  };
  if (plan.supplyCardId) cloned.supplyCardId = plan.supplyCardId;
  return cloned;
}
